/**
 * m3 echo Frontend - Support Form Dialog Component
 *
 * Simple form for users to submit support tickets.
 * Fields: Titel, Nachricht, Anhang (optional)
 *
 * Security:
 * - Strict file type validation (extension + MIME type)
 * - File size limits enforced
 * - Filename sanitization
 */

import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Typography,
  IconButton,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import { supportApi } from '../services/api';

interface SupportFormDialogProps {
  open: boolean;
  onClose: () => void;
}

// Allowed file extensions and their MIME types
const ALLOWED_FILE_TYPES: Record<string, string[]> = {
  '.png': ['image/png'],
  '.jpg': ['image/jpeg'],
  '.jpeg': ['image/jpeg'],
  '.gif': ['image/gif'],
  '.pdf': ['application/pdf'],
  '.txt': ['text/plain'],
  '.doc': ['application/msword'],
  '.docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  '.csv': ['text/csv', 'application/vnd.ms-excel', 'text/plain'],
  '.xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
};

const ALLOWED_EXTENSIONS = Object.keys(ALLOWED_FILE_TYPES);
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_TITLE_LENGTH = 255;
const MAX_MESSAGE_LENGTH = 10000;

/**
 * Validate file extension against allowed list
 */
const isAllowedExtension = (filename: string): boolean => {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return false;
  const ext = filename.substring(lastDot).toLowerCase();
  return ALLOWED_EXTENSIONS.includes(ext);
};

/**
 * Validate file MIME type matches extension
 */
const isValidMimeType = (filename: string, mimeType: string): boolean => {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return false;
  const ext = filename.substring(lastDot).toLowerCase();
  const allowedMimes = ALLOWED_FILE_TYPES[ext];
  if (!allowedMimes) return false;
  return allowedMimes.includes(mimeType);
};

/**
 * Sanitize filename by removing dangerous characters
 */
const sanitizeFilename = (filename: string): string => {
  // Remove path separators and other dangerous characters
  return filename
    .replace(/[/\\]/g, '_')  // Path separators
    .replace(/\.\./g, '_')   // Directory traversal
    .replace(/[<>:"|?*]/g, '_')  // Windows illegal characters
    .replace(/[\x00-\x1f]/g, '');  // Control characters
};

const SupportFormDialog: React.FC<SupportFormDialogProps> = ({ open, onClose }) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [attachmentFilename, setAttachmentFilename] = useState<string | null>(null);
  const [attachmentData, setAttachmentData] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset previous attachment
    setAttachmentFilename(null);
    setAttachmentData(null);

    // Validate file extension
    if (!isAllowedExtension(file.name)) {
      setError(`Dateityp nicht erlaubt. Erlaubte Typen: ${ALLOWED_EXTENSIONS.join(', ')}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate MIME type matches extension
    if (!isValidMimeType(file.name, file.type)) {
      setError('Dateityp stimmt nicht mit Inhalt überein');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      const maxMB = MAX_FILE_SIZE / (1024 * 1024);
      setError(`Datei ist zu gross (max. ${maxMB}MB)`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Check for empty files
    if (file.size === 0) {
      setError('Leere Dateien sind nicht erlaubt');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/png;base64,")
      const base64Data = base64.split(',')[1];

      // Sanitize the filename
      const safeFilename = sanitizeFilename(file.name);

      setAttachmentFilename(safeFilename);
      setAttachmentData(base64Data);
      setError(null);
    };
    reader.onerror = () => {
      setError('Fehler beim Lesen der Datei');
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    reader.readAsDataURL(file);
  }, []);

  const handleRemoveAttachment = () => {
    setAttachmentFilename(null);
    setAttachmentData(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();
    const trimmedMessage = message.trim();

    // Validate required fields
    if (!trimmedTitle || !trimmedMessage) {
      setError('Bitte Titel und Nachricht ausfuellen');
      return;
    }

    // Validate field lengths
    if (trimmedTitle.length > MAX_TITLE_LENGTH) {
      setError(`Titel zu lang (max. ${MAX_TITLE_LENGTH} Zeichen)`);
      return;
    }

    if (trimmedMessage.length > MAX_MESSAGE_LENGTH) {
      setError(`Nachricht zu lang (max. ${MAX_MESSAGE_LENGTH} Zeichen)`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await supportApi.create({
        title: trimmedTitle,
        message: trimmedMessage,
        attachment_filename: attachmentFilename || undefined,
        attachment_data: attachmentData || undefined,
      });

      setSuccess(true);
      // Auto-close after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err: any) {
      console.error('Error creating support ticket:', err);
      // Handle backend validation errors gracefully
      const errorMessage = err.response?.data?.detail || err.message || 'Fehler beim Senden der Anfrage';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setTitle('');
    setMessage('');
    setAttachmentFilename(null);
    setAttachmentData(null);
    setError(null);
    setSuccess(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>Support-Anfrage</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {success ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Ihre Anfrage wurde erfolgreich gesendet. Wir melden uns bei Ihnen.
            </Alert>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Titel"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Betreff Ihrer Anfrage"
                required
                disabled={loading}
                inputProps={{ maxLength: MAX_TITLE_LENGTH }}
                helperText={`${title.length}/${MAX_TITLE_LENGTH}`}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Nachricht"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Beschreiben Sie Ihr Anliegen..."
                required
                multiline
                rows={5}
                disabled={loading}
                inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
                helperText={`${message.length}/${MAX_MESSAGE_LENGTH}`}
                sx={{ mb: 2 }}
              />

              {/* File attachment */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  accept=".png,.jpg,.jpeg,.gif,.pdf,.txt,.doc,.docx,.csv,.xlsx"
                />
                <Button
                  variant="outlined"
                  startIcon={<AttachFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                  size="small"
                >
                  Anhang
                </Button>

                {attachmentFilename && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" color="textSecondary">
                      {attachmentFilename}
                    </Typography>
                    <IconButton size="small" onClick={handleRemoveAttachment} disabled={loading}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>

              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Max. 5MB (Bilder, PDF, Word, Text)
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {success ? 'Schliessen' : 'Abbrechen'}
        </Button>
        {!success && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !title.trim() || !message.trim()}
          >
            {loading ? 'Wird gesendet...' : 'Absenden'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SupportFormDialog;
